import { captureWalletEvent } from "@shared/analytics/walletEvents";
import { walletJourney } from "@shared/analytics/walletJourney";
import { useSessionStore, useWallet } from "@solomon-apps/shared";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useHasBalance } from "@/hooks/portfolio";
import { useWalletRestoreGrace } from "@/hooks/wallet";
import { useRetailAccount } from "@/hooks/yaas/useRetailAccount";

import { RetailYaasLandingPage } from "../Yaas/RetailYaasLandingPage";

import { USDvPage } from "./USDvPage";
import { USDvSkeleton } from "./USDvSkeleton";

/** Matches the skeleton's outer morph duration — keep in sync with USDvSkeleton. */
const MORPH_MS = 400;

type Phase = "skeleton" | "morphing" | "ready";

export function shouldRenderRetailYaas(input: {
    connected: boolean;
    isYaasStatusLoaded: boolean;
    yaasStatus: ReturnType<typeof useSessionStore.getState>["yaasStatus"];
    hasBalance: boolean;
}): boolean {
    return (
        input.connected &&
        ((input.isYaasStatusLoaded && input.yaasStatus !== null) ||
            input.hasBalance)
    );
}

/** Test override states the dispatcher recognizes via `?state=…`. The
 *  landing-style states (`disconnected`, `connected-no-usdv`,
 *  `connected-stables-no-usdv`) render `USDvPage`; the position-aware
 *  states all delegate to `RetailYaasLandingPage`, which already
 *  reads `?state=` from the URL and owns the per-state rendering
 *  branches.
 *
 *  `connected-stables-no-usdv` is a sub-state of
 *  `connected-no-usdv` where the wallet holds USDC / USDT but no
 *  USDv. The dispatcher routes it to `USDvPage` like any landing-
 *  style state; `USDvPage` reads the same `?state=` param to tailor
 *  copy and (eventually) pre-select the BuyWidget input token. */
type HomeState =
    | "disconnected"
    | "connected-no-usdv"
    | "connected-stables-no-usdv"
    | "connected-no-yaas"
    | "apply-below-min"
    | "active"
    | "pending"
    | "opted-in"
    | "below-min"
    | "disabled"
    | "just-distributed"
    | "gold-lp"
    | "gold-lp-wallet";

const STATE_OVERRIDES: ReadonlySet<HomeState> = new Set<HomeState>([
    "disconnected",
    "connected-no-usdv",
    "connected-stables-no-usdv",
    "connected-no-yaas",
    "apply-below-min",
    "active",
    "pending",
    "opted-in",
    "below-min",
    "disabled",
    "just-distributed",
    "gold-lp",
    "gold-lp-wallet"
]);

/** States that render the position-aware surface (left: position card,
 *  right: portfolio summary hero, footnote). All delegate to
 *  `RetailYaasLandingPage` which reads `?state=` for the per-state
 *  rendering branch. */
const POSITION_AWARE_STATES: ReadonlySet<HomeState> = new Set<HomeState>([
    "connected-no-yaas",
    "apply-below-min",
    "active",
    "pending",
    "opted-in",
    "below-min",
    "disabled",
    "just-distributed",
    "gold-lp",
    "gold-lp-wallet"
]);

/**
 * Wallet-aware dispatcher for the `/` route.
 *
 * Two render paths:
 *   - **`USDvPage`** — the marketing landing (coin + hero + buy widget).
 *     Used when the wallet is disconnected OR connected without USDv.
 *   - **`RetailYaasLandingPage`** — the position-aware surface (60/40
 *     guilloche split with a position card on the left and portfolio
 *     summary hero on the right). Used when the wallet has USDv. The
 *     page owns the per-state rendering (connected-no-yaas / pending /
 *     opted-in / active / below-min / etc.) and reads `?state=` from the
 *     URL itself.
 *
 * Test override:
 *   `?state=disconnected | connected-no-usdv | connected-stables-no-usdv |
 *   connected-no-yaas | apply-below-min | active | pending | opted-in |
 *   below-min | disabled | just-distributed` short-circuits the wallet-driven
 *   dispatch so each branch can be iterated on without needing the
 *   actual wallet/backend state. Position-aware states pass straight
 *   through to `RetailYaasLandingPage` (it reads the same param);
 *   landing-style states pass through to `USDvPage` (which also
 *   reads the param to tailor copy when applicable).
 *
 * Flicker prevention:
 *   While the balance answer is indeterminate, a `USDvSkeleton` shell
 *   renders. The override path skips the skeleton.
 *
 * NOTE: As `/yaas` retires and the YaaS surface fully folds into USDv,
 * `RetailYaasLandingPage` will be split / renamed and the home will
 * own the rendering directly. For now delegating preserves visual
 * parity with `/yaas?state=…` while we iterate.
 */
export function USDvHomeRoute(): React.ReactElement {
    const account = useRetailAccount();
    const [params] = useSearchParams();
    const demo = params.has("state") || params.has("simulate");
    return (
        <div className="flex flex-1 flex-col">
            <USDvHomeContent key={demo ? "demo" : account.key} />
        </div>
    );
}

function USDvHomeContent(): React.ReactElement {
    const account = useRetailAccount();
    const [searchParams] = useSearchParams();
    // `?simulate=` scripts the enrollment flow, which lives on the retail
    // page; the sim has no real wallet or balance to route on.
    const isEnrollmentSim = ["enroll", "connect", "full", "short"].includes(
        searchParams.get("simulate") ?? ""
    );
    const stateParam = searchParams.get("state");
    const stateOverride: HomeState | null =
        stateParam && STATE_OVERRIDES.has(stateParam as HomeState)
            ? (stateParam as HomeState)
            : null;

    const walletSettled = useWalletRestoreGrace();
    const { hasBalance, lastKnown } = useHasBalance(walletSettled);
    const { connected } = useWallet();
    const yaasStatus = useSessionStore((s) => s.yaasStatus);
    const isYaasStatusLoaded = useSessionStore((s) => s.isYaasStatusLoaded);

    const [phase, setPhase] = useState<Phase>(() =>
        hasBalance !== null ? "ready" : "skeleton"
    );

    useEffect(() => {
        if (phase === "skeleton" && hasBalance !== null) {
            setPhase("morphing");
            return;
        }
        if (phase === "morphing" && hasBalance === null) {
            setPhase("skeleton");
        }
    }, [phase, hasBalance]);

    useEffect(() => {
        if (phase !== "morphing") return;
        const t = setTimeout(() => setPhase("ready"), MORPH_MS);
        return (): void => clearTimeout(t);
    }, [phase]);

    const homeView =
        connected && isYaasStatusLoaded && yaasStatus !== null
            ? "retail"
            : phase !== "ready"
              ? "loading"
              : shouldRenderRetailYaas({
                      connected,
                      isYaasStatusLoaded,
                      yaasStatus,
                      hasBalance: lastKnown ?? false
                  })
                ? "retail"
                : "landing";
    const lastView = useRef("");
    useEffect(() => {
        if (stateOverride || homeView !== "loading") return;
        const key = `${homeView}:${connected}`;
        if (lastView.current === key) return;
        lastView.current = key;
        captureWalletEvent(
            "wallet:home_viewed",
            { view: homeView, wallet_connected: connected },
            walletJourney.owner()
        );
    }, [homeView, connected, stateOverride]);

    if (isEnrollmentSim) return <RetailYaasLandingPage />;

    if (stateOverride) {
        return POSITION_AWARE_STATES.has(stateOverride) ? (
            <RetailYaasLandingPage />
        ) : (
            <USDvPage />
        );
    }

    if (account.isSquads)
        return account.ready ? (
            <RetailYaasLandingPage />
        ) : (
            <main className="flex flex-1 items-center justify-center p-6">
                <Text variant="body" role="status">
                    {account.notice ?? "Checking your Squads wallet…"}
                </Text>
            </main>
        );

    // Account state outranks balance. An enrolled wallet can have no USDv and
    // no positions while it waits for normal Program execution.
    if (connected && isYaasStatusLoaded && yaasStatus !== null) {
        return <RetailYaasLandingPage />;
    }

    if (phase !== "ready") {
        const resolved = lastKnown ?? false;
        const targetLayout: "positions" | "landing" =
            phase === "morphing"
                ? resolved
                    ? "positions"
                    : "landing"
                : "positions";
        return (
            <USDvSkeleton
                targetLayout={targetLayout}
                exiting={phase === "morphing"}
            />
        );
    }

    // No override: wallet-driven dispatch.
    //   - disconnected OR connected-with-no-USDv → USDvPage (landing)
    //   - connected with USDv → RetailYaasLandingPage (it figures out
    //     pending vs active vs connected-no-yaas based on yaasStatus)
    if (
        !shouldRenderRetailYaas({
            connected,
            isYaasStatusLoaded,
            yaasStatus,
            hasBalance: lastKnown ?? false
        })
    ) {
        return <USDvPage />;
    }
    return <RetailYaasLandingPage />;
}
